import React, { useRef } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { uploadSDEFile } from '../sdeSupabaseService';

const StepContainer = styled.div`max-width: 700px; margin: 0 auto;`;
const StepTitle = styled.h2`font-size: 1.5rem; font-weight: 600; color: #1F2937; margin-bottom: 0.5rem;`;
const StepSubtitle = styled.p`color: #6B7280; margin-bottom: 2rem; font-size: 0.95rem;`;
const FormGroup = styled.div`margin-bottom: 1.25rem;`;
const Label = styled.label`
  display: block; font-size: 0.95rem; font-weight: 500; color: #374151; margin-bottom: 0.4rem;
  ${props => props.required && `&::after { content: ' *'; color: #EF4444; }`}
`;
const Input = styled.input`
  width: 100%; padding: 0.75rem 1rem; font-size: 0.95rem; border: 2px solid #E5E7EB; border-radius: 8px;
  background: #F9FAFB; &:focus { outline: none; border-color: #2563EB; background: white; }
`;
const Textarea = styled.textarea`
  width: 100%; padding: 0.75rem 1rem; font-size: 0.95rem; border: 2px solid #E5E7EB; border-radius: 8px;
  background: #F9FAFB; min-height: 80px; resize: vertical;
  &:focus { outline: none; border-color: #2563EB; }
`;
const Row = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const ProjectCard = styled.div`
  background: white; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1.5rem; margin-bottom: 1rem;
`;
const ProjectHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;
  h4 { margin: 0; font-size: 1rem; color: #374151; }
`;
const RemoveBtn = styled.button`
  background: #FEE2E2; color: #DC2626; border: none; padding: 0.4rem 0.8rem; border-radius: 6px;
  font-size: 0.85rem; cursor: pointer; font-weight: 500;
  &:hover { background: #FECACA; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const AddButton = styled.button`
  width: 100%; padding: 1rem; background: #F0F7FF; border: 2px dashed #93C5FD; border-radius: 12px;
  color: #2563EB; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
  &:hover { background: #DBEAFE; border-color: #2563EB; }
`;
const UploadZone = styled.div`
  border: 2px dashed #D1D5DB; border-radius: 8px; padding: 1.5rem; text-align: center;
  cursor: pointer; transition: all 0.2s; background: #FAFAFA;
  &:hover { border-color: #93C5FD; background: #F0F7FF; }
  p { color: #6B7280; margin: 0.25rem 0; font-size: 0.9rem; }
`;
const FilePreview = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 0.75rem;
`;
const FileThumb = styled.div`
  position: relative; width: 100px; height: 100px; border-radius: 8px; overflow: hidden;
  border: 1px solid #E5E7EB;
  img { width: 100%; height: 100%; object-fit: cover; }
  button {
    position: absolute; top: 4px; right: 4px; background: rgba(239,68,68,0.9); color: white;
    border: none; border-radius: 50%; width: 22px; height: 22px; cursor: pointer;
    font-size: 0.7rem; display: flex; align-items: center; justify-content: center;
  }
`;
const InfoBanner = styled.div`
  background: #EFF6FF; border: 1px solid #BFDBFE; border-radius: 8px; padding: 0.75rem 1rem;
  margin-bottom: 1.5rem; color: #1E40AF; font-size: 0.9rem;
`;
const RadioGroup = styled.div`
  display: flex; gap: 1rem; margin-top: 0.25rem;
`;
const RadioLabel = styled.label`
  display: flex; align-items: center; gap: 0.5rem; cursor: pointer; font-size: 0.9rem; color: #374151;
  padding: 4px 8px; border-radius: 6px; transition: background 0.15s;
  &:hover { background: #F3F4F6; }
  input { accent-color: #2563EB; width: 16px; height: 16px; }
`;

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB per file
const MAX_TOTAL_SIZE = 500 * 1024 * 1024; // 500MB total
const ACCEPTED_FORMATS = '.jpg,.jpeg,.png,.webp,.pdf,.mp4,.mov,image/jpeg,image/png,image/webp,application/pdf,video/mp4,video/quicktime';
const MIN_PROJECTS = 3;
const MAX_PROJECTS = 5;

const PopBStep3_Portfolio = ({ data, updateData, lang = 'fr', eventId }) => {
  const projects = data.portfolio_projects || [];
  const fileInputRefs = useRef({});

  const addProject = () => {
    updateData({
      portfolio_projects: [...projects, {
        id: uuidv4(), title: '', type: '', country: '', city: '',
        description: '', files: [], confidentiality: 'public',
      }]
    });
  };

  const updateProject = (id, field, value) => {
    updateData({
      portfolio_projects: projects.map(p => p.id === id ? { ...p, [field]: value } : p)
    });
  };

  const removeProject = (id) => {
    updateData({ portfolio_projects: projects.filter(p => p.id !== id) });
  };

  const getTotalFileSize = () => {
    let total = 0;
    projects.forEach(p => {
      (p.files || []).forEach(f => { total += f.size || 0; });
    });
    return total;
  };

  const handleFileUpload = async (projectId, files) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    const newFiles = [...(project.files || [])];
    let currentTotal = getTotalFileSize();

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        alert(lang === 'fr'
          ? `Le fichier "${file.name}" dépasse la limite de 50 Mo.`
          : `File "${file.name}" exceeds the 50MB limit.`);
        continue;
      }
      if (currentTotal + file.size > MAX_TOTAL_SIZE) {
        alert(lang === 'fr'
          ? 'La taille totale des fichiers dépasse la limite de 500 Mo.'
          : 'Total file size exceeds the 500MB limit.');
        break;
      }

      const safeName = file.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `portfolio/${eventId || 'draft'}/${projectId}/${Date.now()}-${safeName}`;
      const result = await uploadSDEFile(file, path);
      if (result.error) {
        console.error('Upload error:', result.error);
        alert(lang === 'fr'
          ? `Erreur lors du téléchargement de "${file.name}". Veuillez réessayer ou contacter design@via.fr.`
          : `Error uploading "${file.name}". Please try again or contact design@via.fr.`);
        continue;
      }
      if (result.data) {
        newFiles.push({
          path: result.data.path,
          publicUrl: result.data.publicUrl,
          name: file.name,
          mimetype: file.type,
          size: file.size,
        });
        currentTotal += file.size;
      }
    }

    updateProject(projectId, 'files', newFiles);
  };

  const removeFile = (projectId, fileIndex) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    const newFiles = project.files.filter((_, i) => i !== fileIndex);
    updateProject(projectId, 'files', newFiles);
  };

  const isVideo = (mimetype) => mimetype?.startsWith('video/');

  return (
    <StepContainer>
      <StepTitle>{lang === 'fr' ? 'Votre portfolio' : 'Your Portfolio'}</StepTitle>
      <StepSubtitle>
        {lang === 'fr'
          ? 'Présentez vos meilleurs projets avec des images, PDF ou vidéos'
          : 'Showcase your best projects with images, PDFs or videos'}
      </StepSubtitle>

      <InfoBanner>
        {lang === 'fr'
          ? `Ajoutez ${MIN_PROJECTS} à ${MAX_PROJECTS} projets qui représentent votre travail. Formats acceptés : images (JPG, PNG), PDF, vidéos (MP4, MOV). Max 50 Mo par fichier, 500 Mo au total.`
          : `Add ${MIN_PROJECTS} to ${MAX_PROJECTS} projects that represent your work. Accepted formats: images (JPG, PNG), PDF, videos (MP4, MOV). Max 50MB per file, 500MB total.`}
      </InfoBanner>

      {projects.map((project, index) => (
        <ProjectCard key={project.id}>
          <ProjectHeader>
            <h4>{lang === 'fr' ? `Projet ${index + 1}` : `Project ${index + 1}`}</h4>
            <RemoveBtn
              onClick={() => removeProject(project.id)}
              disabled={projects.length <= MIN_PROJECTS}
              title={projects.length <= MIN_PROJECTS ? (lang === 'fr' ? `Minimum ${MIN_PROJECTS} projets requis` : `Minimum ${MIN_PROJECTS} projects required`) : ''}
            >
              {lang === 'fr' ? 'Supprimer' : 'Remove'}
            </RemoveBtn>
          </ProjectHeader>

          <UploadZone onClick={() => fileInputRefs.current[project.id]?.click()}>
            <p style={{ fontSize: '1.5rem' }}>📎</p>
            <p>{lang === 'fr' ? 'Cliquez pour ajouter des fichiers (images, PDF, vidéos)' : 'Click to add files (images, PDFs, videos)'}</p>
            <p style={{ fontSize: '0.8rem', color: '#9CA3AF' }}>JPG, PNG, PDF, MP4, MOV</p>
            <input
              ref={el => fileInputRefs.current[project.id] = el}
              type="file" accept={ACCEPTED_FORMATS} multiple style={{ display: 'none' }}
              onChange={e => handleFileUpload(project.id, Array.from(e.target.files))}
            />
          </UploadZone>

          {project.files?.length > 0 && (
            <FilePreview>
              {project.files.map((file, fi) => (
                <FileThumb key={fi}>
                  {file.mimetype?.startsWith('image/') ? (
                    <img src={file.publicUrl} alt={file.name} />
                  ) : isVideo(file.mimetype) ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#F3F4F6', fontSize: '1.5rem' }}>
                      🎬
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#F3F4F6', fontSize: '0.7rem', padding: '0.5rem', textAlign: 'center', color: '#6B7280' }}>
                      {file.name}
                    </div>
                  )}
                  <button onClick={() => removeFile(project.id, fi)}>&times;</button>
                </FileThumb>
              ))}
            </FilePreview>
          )}

          <FormGroup style={{ marginTop: '1rem' }}>
            <Label required>{lang === 'fr' ? 'Nom du projet' : 'Project Name'}</Label>
            <Input value={project.title || ''} onChange={e => updateProject(project.id, 'title', e.target.value)} />
          </FormGroup>
          <Row>
            <FormGroup>
              <Label>{lang === 'fr' ? 'Type' : 'Type'}</Label>
              <Input value={project.type || ''} onChange={e => updateProject(project.id, 'type', e.target.value)} placeholder={lang === 'fr' ? 'Ex: Design mobilier...' : 'E.g., Furniture design...'} />
            </FormGroup>
            <FormGroup>
              <Label>{lang === 'fr' ? 'Année' : 'Year'}</Label>
              <Input type="number" value={project.year || ''} onChange={e => updateProject(project.id, 'year', e.target.value)} placeholder="2024" />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup>
              <Label>{lang === 'fr' ? 'Pays' : 'Country'}</Label>
              <Input value={project.country || ''} onChange={e => updateProject(project.id, 'country', e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>{lang === 'fr' ? 'Ville' : 'City'}</Label>
              <Input value={project.city || ''} onChange={e => updateProject(project.id, 'city', e.target.value)} />
            </FormGroup>
          </Row>
          <FormGroup>
            <Label>{lang === 'fr' ? 'Description' : 'Description'}</Label>
            <Textarea value={project.description || ''} onChange={e => updateProject(project.id, 'description', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label required>{lang === 'fr' ? 'Confidentialité' : 'Confidentiality'}</Label>
            <RadioGroup>
              <RadioLabel>
                <input type="radio" checked={project.confidentiality !== 'confidentiel'} onChange={() => updateProject(project.id, 'confidentiality', 'public')} />
                Public
              </RadioLabel>
              <RadioLabel>
                <input type="radio" checked={project.confidentiality === 'confidentiel'} onChange={() => updateProject(project.id, 'confidentiality', 'confidentiel')} />
                {lang === 'fr' ? 'Confidentiel' : 'Confidential'}
              </RadioLabel>
            </RadioGroup>
          </FormGroup>
        </ProjectCard>
      ))}

      {projects.length < MAX_PROJECTS && (
        <AddButton onClick={addProject}>
          + {lang === 'fr' ? 'Ajouter un projet' : 'Add a project'} ({projects.length}/{MAX_PROJECTS})
        </AddButton>
      )}

      {projects.length < MIN_PROJECTS && (
        <InfoBanner style={{ marginTop: '1rem', background: '#FEF3C7', borderColor: '#FCD34D', color: '#92400E' }}>
          {lang === 'fr'
            ? `Minimum ${MIN_PROJECTS} projets requis. Ajoutez encore ${MIN_PROJECTS - projects.length} projet(s).`
            : `Minimum ${MIN_PROJECTS} projects required. Add ${MIN_PROJECTS - projects.length} more project(s).`}
        </InfoBanner>
      )}
    </StepContainer>
  );
};

export default PopBStep3_Portfolio;
